import { TIPTAP_ACTION_BUTTON_TYPE, TIPTAP_LINK_BUTTON_TYPE } from './../ricos-content';
import type { PluginToolbarButtonConfig } from './../ricos-types';
import {
  PLUGIN_TOOLBAR_ALIGNMENT_BUTTON,
  PLUGIN_TOOLBAR_DELETE_BUTTON,
  PLUGIN_TOOLBAR_REPLACE_BUTTON,
  PLUGIN_TOOLBAR_SETTINGS_BUTTON,
  PLUGIN_TOOLBAR_SIZE_BUTTON,
} from './../_shared/data-hooks';
import { PLUGIN_TOOLBAR_BUTTON_ID } from './../editor-common';
import {
  getNodeAlignmentResolver,
  getNodeInSelectionResolver,
  getNodeSizeResolver,
  getNodeTextWrapResolver,
} from './../toolbars-v3';

type PluginButtonId = (typeof PLUGIN_TOOLBAR_BUTTON_ID)[keyof typeof PLUGIN_TOOLBAR_BUTTON_ID];

type IPluginToolbarButtonsConfig = (props: {
  isMobile: boolean;
}) => Record<PluginButtonId, PluginToolbarButtonConfig>;

export const toolbarButtonsConfig: IPluginToolbarButtonsConfig = ({ isMobile }) => ({
  delete: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.DELETE,
    type: 'toggle',
    attributes: {
      selectedNode: getNodeInSelectionResolver,
    },
    presentation: {
      dataHook: PLUGIN_TOOLBAR_DELETE_BUTTON,
    },
    commands: {
      delete: ({ tiptapEditor, attributes: { selectedNode } }) => {
        tiptapEditor.chain().focus().deleteNode(selectedNode.attrs.id).run();
      },
    },
  },
  alignment: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.ALIGNMENT,
    type: 'modal',
    attributes: {
      nodeAlignment: getNodeAlignmentResolver,
      nodeTextWrap: getNodeTextWrapResolver,
      visible: {
        id: 'IS_ALIGNMENT_BUTTON_VISIBLE',
        resolve: () => !isMobile,
      },
    },
    presentation: {
      dataHook: PLUGIN_TOOLBAR_ALIGNMENT_BUTTON,
    },
    commands: {
      setAlignment: ({ tiptapEditor, value, attributes: { selectedNode } }) => {
        tiptapEditor
          .chain()
          .focus()
          .setNodeAlignment(value)
          .command(({ commands }: $TSFixMe) => {
            const isCustomWidthSpecified = selectedNode?.attrs.containerData?.width?.custom;
            const excludedNodeTypes = [TIPTAP_LINK_BUTTON_TYPE, TIPTAP_ACTION_BUTTON_TYPE];

            if (!isCustomWidthSpecified && !excludedNodeTypes.includes(selectedNode.type.name)) {
              commands.setNodeSize('SMALL');
            }

            return true;
          })
          .run();
      },
      toggleTextWrap: ({ tiptapEditor }) => {
        tiptapEditor.chain().focus().toggleNodeTextWrap().run();
      },
      canToggleTextWrap: ({ tiptapEditor }) => {
        return tiptapEditor.can().toggleNodeTextWrap();
      },
    },
  },
  size: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.SIZE,
    type: 'modal',
    attributes: {
      nodeSize: getNodeSizeResolver,
      visible: {
        id: 'IS_SIZE_BUTTON_VISIBLE',
        resolve: () => !isMobile,
      },
    },
    presentation: {
      dataHook: PLUGIN_TOOLBAR_SIZE_BUTTON,
    },
    commands: {
      setSize: ({ tiptapEditor, value }) => {
        tiptapEditor.chain().focus().setNodeSize(value).setNodeAlignment('CENTER').run();
      },
    },
  },
  settings: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.SETTINGS,
    type: 'modal',
    presentation: {
      dataHook: PLUGIN_TOOLBAR_SETTINGS_BUTTON,
    },
    attributes: {
      selectedNode: getNodeInSelectionResolver,
    },
    commands: {},
  },
  replace: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.REPLACE,
    type: 'toggle',
    presentation: {
      dataHook: PLUGIN_TOOLBAR_REPLACE_BUTTON,
    },
    attributes: {
      selectedNode: getNodeInSelectionResolver,
    },
    commands: {},
  },
  separator: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    type: 'separator',
    presentation: {},
    attributes: {},
    commands: {},
  },
});
